import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { api, mediaUrl, getToken } from "../lib/api";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

function uid() { return Math.random().toString(36).slice(2) + Date.now(); }

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sessionRef = useRef(null);
  const qualifiedRef = useRef(false);

  const reportStream = useCallback((track, dur, completed) => {
    if (!track || !getToken()) return;
    api.post("/music/stream", {
      track_id: track.id, session_id: sessionRef.current || uid(),
      duration: dur, completed,
    }).catch(() => {});
  }, []);

  const playTrack = useCallback((track, list) => {
    const q = list && list.length ? list : [track];
    const idx = q.findIndex((t) => t.id === track.id);
    setQueue(q);
    setIndex(idx < 0 ? 0 : idx);
    setCurrent(track);
    sessionRef.current = uid();
    qualifiedRef.current = false;
    setExpanded(false);
    setTimeout(() => {
      const a = audioRef.current;
      if (a) { a.src = mediaUrl(track.audio_url); a.play().catch(() => {}); }
    }, 0);
  }, []);

  const next = useCallback(() => {
    if (!queue.length) return;
    let n = shuffle ? Math.floor(Math.random() * queue.length) : index + 1;
    if (n >= queue.length) n = 0;
    const t = queue[n];
    if (t) { setIndex(n); setCurrent(t); sessionRef.current = uid(); qualifiedRef.current = false;
      setTimeout(() => { const a = audioRef.current; if (a) { a.src = mediaUrl(t.audio_url); a.play().catch(() => {}); } }, 0); }
  }, [queue, index, shuffle]);

  const prev = useCallback(() => {
    if (!queue.length) return;
    const n = index - 1 < 0 ? queue.length - 1 : index - 1;
    const t = queue[n];
    if (t) { setIndex(n); setCurrent(t); sessionRef.current = uid(); qualifiedRef.current = false;
      setTimeout(() => { const a = audioRef.current; if (a) { a.src = mediaUrl(t.audio_url); a.play().catch(() => {}); } }, 0); }
  }, [queue, index]);

  const toggle = useCallback(() => {
    const a = audioRef.current; if (!a || !current) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  }, [current]);

  const seek = useCallback((t) => { const a = audioRef.current; if (a) a.currentTime = t; }, []);

  // Duck/resume music when competing media (e.g. VStar TV) plays
  const duckedRef = useRef(false);
  const pauseForMedia = useCallback(() => {
    const a = audioRef.current;
    if (a && current && !a.paused) { a.pause(); duckedRef.current = true; }
  }, [current]);
  const resumeFromMedia = useCallback(() => {
    const a = audioRef.current;
    if (a && duckedRef.current) { duckedRef.current = false; a.play().catch(() => {}); }
  }, []);

  useEffect(() => { const a = audioRef.current; if (a) a.volume = volume; }, [volume]);

  // Lock-screen / OS media controls (Media Session API): show artist + title + art.
  useEffect(() => {
    if (!("mediaSession" in navigator) || !window.MediaMetadata) return;
    if (!current) { navigator.mediaSession.metadata = null; return; }
    const art = current.cover_url ? mediaUrl(current.cover_url) : null;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: current.title || "Untitled",
        artist: current.artist?.display_name || current.artist?.username || "VirtuStars",
        album: "VirtuStars Music",
        artwork: art ? [96, 128, 192, 256, 384, 512].map((s) => ({ src: art, sizes: `${s}x${s}` })) : [],
      });
    } catch {}
  }, [current]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch {} };
    set("play", () => toggle());
    set("pause", () => toggle());
    set("previoustrack", () => prev());
    set("nexttrack", () => next());
    set("seekto", (d) => { if (d && d.seekTime != null) seek(d.seekTime); });
  }, [toggle, prev, next, seek]);

  useEffect(() => {
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = playing ? "playing" : "paused";
  }, [playing]);

  const onTime = () => {
    const a = audioRef.current; if (!a) return;
    setProgress(a.currentTime); setDuration(a.duration || 0);
    if ("mediaSession" in navigator && a.duration && isFinite(a.duration)) {
      try { navigator.mediaSession.setPositionState({ duration: a.duration, position: Math.min(a.currentTime, a.duration), playbackRate: a.playbackRate || 1 }); } catch {}
    }
    if (!qualifiedRef.current && a.currentTime >= 30) {
      qualifiedRef.current = true;
      reportStream(current, a.currentTime, false);
    }
  };
  const onEnded = () => {
    reportStream(current, duration, true);
    if (repeat) { const a = audioRef.current; if (a) { a.currentTime = 0; a.play(); } }
    else next();
  };

  return (
    <PlayerCtx.Provider value={{
      current, playing, progress, duration, volume, shuffle, repeat, queue, expanded,
      playTrack, next, prev, toggle, seek, setVolume, setShuffle, setRepeat, setExpanded,
      pauseForMedia, resumeFromMedia,
    }}>
      <audio ref={audioRef}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={onTime} onLoadedMetadata={onTime} onEnded={onEnded} />
      {children}
    </PlayerCtx.Provider>
  );
}
